import React, { useEffect, useState } from "react";
import { useUIContext } from "../../../context/UIContext";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import useSchoolYearPeriod from "../../../hooks/useSchoolYearPeriod";

const SLOT_KEYS = ["1", "2", "3", "4"];

function normHierarchyId(v) {
  if (v === null || v === undefined || v === "" || v === "0" || v === 0) {
    return null;
  }
  return String(v);
}

/** API school year period row for current year + hierarchy, else whole-school row, else single row for year. */
function findSchoolYearBoundsRow(periodData, schoolYearId, hierarchyOneId, hierarchyTwoId) {
  if (!periodData?.length || schoolYearId === "" || schoolYearId == null) {
    return null;
  }
  const yearRows = periodData.filter(
    (r) => String(r.school_year_id) === String(schoolYearId)
  );
  if (!yearRows.length) {
    return null;
  }
  const h1 = normHierarchyId(hierarchyOneId);
  const h2 = normHierarchyId(hierarchyTwoId);
  const exact = yearRows.find(
    (r) =>
      normHierarchyId(r.applied_hierarchy_one) === h1 &&
      normHierarchyId(r.applied_hierarchy_two) === h2
  );
  if (exact) {
    return exact;
  }
  const wholeSchool = yearRows.find(
    (r) =>
      normHierarchyId(r.applied_hierarchy_one) === null &&
      normHierarchyId(r.applied_hierarchy_two) === null
  );
  if (wholeSchool) {
    return wholeSchool;
  }
  if (yearRows.length === 1) {
    return yearRows[0];
  }
  return null;
}

function toComparableDateKey(s) {
  if (!s || !String(s).trim()) {
    return null;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) {
    return null;
  }
  const slice = String(s).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(slice)) {
    return slice;
  }
  return d.toISOString().slice(0, 10);
}

function isDateWithinSchoolYearBounds(dateKey, syStartKey, syEndKey) {
  if (!dateKey || !syStartKey || !syEndKey) {
    return false;
  }
  return dateKey >= syStartKey && dateKey <= syEndKey;
}

function emptySlots() {
  return {
    "1": { title: "", startDate: "", endDate: "" },
    "2": { title: "", startDate: "", endDate: "" },
    "3": { title: "", startDate: "", endDate: "" },
    "4": { title: "", startDate: "", endDate: "" },
  };
}

function parseSlotsFromDataValue(dataValue) {
  const next = emptySlots();
  if (!dataValue || typeof dataValue !== "object") {
    return next;
  }
  SLOT_KEYS.forEach((k) => {
    const slot = dataValue[k];
    if (slot && typeof slot === "object") {
      next[k] = {
        title: typeof slot.title === "string" ? slot.title : "",
        startDate: typeof slot.startDate === "string" ? slot.startDate : "",
        endDate:
          typeof slot.endDate === "string"
            ? slot.endDate
            : typeof slot.endData === "string"
              ? slot.endData
              : "",
      };
    }
  });
  return next;
}

function buildDataValue(slots) {
  const value = {};
  SLOT_KEYS.forEach((k) => {
    value[k] = {
      title: slots[k].title.trim(),
      startDate: slots[k].startDate,
      endDate: slots[k].endDate,
    };
  });
  return value;
}

export default function QuarterPeriod({
  schoolYearInfo,
  schoolId,
  quarterPeriodRows,
  createOrUpdateFlexibleData,
}) {
  const { setIsLoadingModal } = useUIContext();
  const { user } = useAuthContext();
  const { t } = useTranslation();

  const {
    schoolYearPeriodQuery: { data: schoolYearPeriodData },
  } = useSchoolYearPeriod({ schoolid: schoolId });

  const [slots, setSlots] = useState(emptySlots);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    const yearId = schoolYearInfo?.schoolYearId;
    if (yearId === "" || yearId === null || yearId === undefined) {
      setSlots(emptySlots());
      return;
    }
    const existingRow = quarterPeriodRows?.find(
      (r) => r.school_year_id?.toString() === yearId.toString()
    );
    if (existingRow?.data_value) {
      setSlots(parseSlotsFromDataValue(existingRow.data_value));
    } else {
      setSlots(emptySlots());
    }
  }, [schoolYearInfo?.schoolYearId, quarterPeriodRows]);

  const handleSlotChange = (key, field, value) => {
    setSlots((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
    setJustSaved(false);
  };

  const handleSave = () => {
    const yearId = schoolYearInfo?.schoolYearId;
    if (
      yearId === "" ||
      yearId === null ||
      yearId === undefined ||
      !/^\d+$/.test(String(yearId))
    ) {
      alert(
        t("admin.periodSetting.quarterSelectSchoolYear") ||
          "학년도를 선택해 주세요."
      );
      return;
    }

    for (const k of SLOT_KEYS) {
      const row = slots[k];
      if (
        row.startDate &&
        String(row.startDate).trim() &&
        isNaN(new Date(row.startDate).getTime())
      ) {
        alert(
          t("admin.periodSetting.invalidStartDate") ||
            "유효하지 않은 시작날짜입니다."
        );
        return;
      }
      if (
        row.endDate &&
        String(row.endDate).trim() &&
        isNaN(new Date(row.endDate).getTime())
      ) {
        alert(
          t("admin.periodSetting.invalidEndDate") ||
            "유효하지 않은 종료날짜입니다."
        );
        return;
      }
      if (
        row.startDate &&
        row.endDate &&
        !isNaN(new Date(row.startDate).getTime()) &&
        !isNaN(new Date(row.endDate).getTime()) &&
        new Date(row.startDate) > new Date(row.endDate)
      ) {
        alert(
          t("admin.periodSetting.quarterStartBeforeEnd", { slot: k }) ||
            `제${k}분기 시작일이 종료일보다 늦을 수 없습니다.`
        );
        return;
      }
    }

    const anyQuarterDate = SLOT_KEYS.some(
      (k) =>
        (slots[k].startDate && String(slots[k].startDate).trim()) ||
        (slots[k].endDate && String(slots[k].endDate).trim())
    );

    if (anyQuarterDate) {
      const boundsRow = findSchoolYearBoundsRow(
        schoolYearPeriodData,
        yearId,
        schoolYearInfo?.hierarchyOneId,
        schoolYearInfo?.hierarchyTwoId
      );
      if (!boundsRow) {
        alert(
          t("admin.periodSetting.quarterNeedSchoolYearBounds") ||
            "선택한 학년도·계층에 해당하는 학년도 날짜(시작·종료)가 없습니다. 상단 학년도 날짜설정을 먼저 등록한 뒤 분기 날짜를 입력해 주세요."
        );
        return;
      }
      const syStartKey = toComparableDateKey(boundsRow.start_date);
      const syEndKey = toComparableDateKey(boundsRow.end_date);
      if (!syStartKey || !syEndKey || syStartKey > syEndKey) {
        alert(
          t("admin.periodSetting.quarterSchoolYearBoundsInvalid") ||
            "학년도 날짜 설정의 시작일·종료일이 올바르지 않습니다."
        );
        return;
      }
      for (const k of SLOT_KEYS) {
        const row = slots[k];
        for (const field of ["startDate", "endDate"]) {
          const raw = row[field];
          if (!raw || !String(raw).trim()) {
            continue;
          }
          const dKey = toComparableDateKey(raw);
          if (!dKey) {
            continue;
          }
          if (!isDateWithinSchoolYearBounds(dKey, syStartKey, syEndKey)) {
            alert(
              t("admin.periodSetting.quarterDateOutsideSchoolYear", {
                slot: k,
                syStart: syStartKey,
                syEnd: syEndKey,
              }) ||
                `제${k}분기 날짜는 학년도 기간(${syStartKey} ~ ${syEndKey}) 안에만 설정할 수 있습니다.`
            );
            return;
          }
        }
      }
    }

    const existingRow = quarterPeriodRows?.find(
      (r) => r.school_year_id?.toString() === yearId.toString()
    );

    const payload = {
      ...(existingRow?.id ? { id: existingRow.id } : {}),
      schoolid: schoolId,
      school_year_id: parseInt(String(yearId), 10),
      main_category: "quarterPeriod",
      sub_category: null,
      term_id: null,
      data_value: buildDataValue(slots),
      ...(user?.username ? { updatedBy: user.username } : {}),
    };

    setIsLoadingModal(true);
    createOrUpdateFlexibleData.mutate(payload, {
      onSuccess: () => {
        setIsLoadingModal(false);
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 1500);
      },
      onError: (error) => {
        setIsLoadingModal(false);
        alert(
          (t("common.messages.errorAlert") ||
            "다음과 같은 에러가 발생했습니다. 이 화면을 찍어 관리자에게 문의하세요. ") +
            (error?.message || "")
        );
      },
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-6">
          <h3>
            {t("admin.periodSetting.quarterSectionTitle") ||
              "출석용 분기(Quarter) 기간"}
          </h3>
        </div>
      </div>
      <table className="table table-bordered text-center">
        <thead>
          <tr className="table-secondary">
            <th style={{ width: "12%" }}>
              {t("admin.periodSetting.applied") || "적용"}
              <br />
              {t("common.labels.schoolYear") || "학년도"}
            </th>
            <th style={{ width: "10%" }}>
              {t("admin.periodSetting.quarterSlot") || "분기"}
            </th>
            <th style={{ width: "22%" }}>
              {t("admin.periodSetting.periodName") || "기간 명칭"}
            </th>
            <th style={{ width: "18%" }}>
              {t("common.labels.startDate") || "시작날짜"}
            </th>
            <th style={{ width: "18%" }}>
              {t("common.labels.endDate") || "종료날짜"}
            </th>
            <th style={{ width: "20%" }}>
              {t("common.labels.save") || "저장"}
            </th>
          </tr>
        </thead>
        <tbody>
          {SLOT_KEYS.map((k, index) => (
            <tr key={k}>
              {index === 0 ? (
                <td rowSpan={SLOT_KEYS.length}>
                  {schoolYearInfo?.schoolYear ?? ""}
                </td>
              ) : null}
              <td>
                {t("admin.periodSetting.quarterN", { n: k }) || `제${k}분기`}
              </td>
              <td>
                <input
                  className="input-like-span"
                  type="text"
                  name={`title-${k}`}
                  value={slots[k].title}
                  onChange={(e) =>
                    handleSlotChange(k, "title", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  className="input-like-span"
                  type="date"
                  name={`startDate-${k}`}
                  value={slots[k].startDate}
                  onChange={(e) =>
                    handleSlotChange(k, "startDate", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  className="input-like-span"
                  type="date"
                  name={`endDate-${k}`}
                  value={slots[k].endDate}
                  onChange={(e) =>
                    handleSlotChange(k, "endDate", e.target.value)
                  }
                />
              </td>
              {index === 0 ? (
                <td rowSpan={SLOT_KEYS.length}>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="btn btn-sm p-1 me-1 btn-primary"
                  >
                    {justSaved
                      ? t("common.labels.savedWell") || "저장완료"
                      : t("common.labels.save") || "저장"}
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-muted small mb-0">
        {t("admin.periodSetting.quarterSaveHint") ||
          "위에서 선택한 학년도에 맞춰 분기별로 입력합니다. 명칭·날짜는 비워 둔 채로 저장할 수 있으며, 둘 다 입력한 경우에만 시작일이 종료일보다 늦을 수 없습니다."}
      </p>
    </>
  );
}
